import React from "react";

// Compact one-line location summary for Job/Task list rows and the Kanban board.
//
// Works for two entity shapes:
//  - JOB: location is EITHER a linked Site Location (CustomerSiteLocation_FullName)
//    OR a typed one-off address, chosen via FlagUseManualAddress (0/1).
//  - TASK: 3-way via TaskLocationMode:
//      0 = inherit from the linked Job  (resolved from the Job_* fields below)
//      1 = the Task's own Site Location (CustomerSiteLocation_FullName)
//      2 = the Task's own manual address (AddressLine*)
//
// It condenses whichever applies to a single line: a pin icon then either the
// site location name, or the address as "Line1, Town, PostCode". Mirrors the
// single-job / single-task Customer Details card.

const truthyFlag = (v) => v === 1 || v === "1" || v === true;

const addressOneLiner = (line1, city, postcode) =>
  [line1, city, postcode]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const siteName = (v) => (v ? String(v).trim() : "");

// Returns the plain text summary (used for tooltips / when a caller wants the string only).
export const getLocationSummaryText = (entity) => {
  if (!entity) return "";

  // TASK — 3-way mode.
  if (entity.TaskLocationMode !== undefined && entity.TaskLocationMode !== null) {
    const mode = Number(entity.TaskLocationMode);
    if (mode === 2) {
      return addressOneLiner(
        entity.AddressLine1,
        entity.AddressCity,
        entity.AddressPostCode
      );
    }
    if (mode === 1) {
      return siteName(entity.CustomerSiteLocation_FullName);
    }
    // mode 0 = inherit from the linked Job (blank if no Job).
    if (truthyFlag(entity.Job_FlagUseManualAddress)) {
      return addressOneLiner(
        entity.Job_AddressLine1,
        entity.Job_AddressCity,
        entity.Job_AddressPostCode
      );
    }
    return siteName(entity.Job_CustomerSiteLocation_FullName);
  }

  // JOB — boolean.
  if (truthyFlag(entity.FlagUseManualAddress)) {
    return addressOneLiner(
      entity.AddressLine1,
      entity.AddressCity,
      entity.AddressPostCode
    );
  }
  return siteName(entity.CustomerSiteLocation_FullName);
};

const LocationSummaryLine = ({ entity, className = "" }) => {
  const text = getLocationSummaryText(entity);
  if (!text) return null;
  return (
    <div
      className={`d-flex align-items-center text-muted small ${className}`}
      title={text}
    >
      <i className="ri-map-pin-line me-1" />
      <span className="text-truncate">{text}</span>
    </div>
  );
};

export default LocationSummaryLine;
